import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { useAuthStore } from '../lib/auth'
import { supabase } from '../lib/supabase'

export default function VerifyEmail() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const email = location.state?.email
  const resendVerification = useAuthStore((s) => s.resendVerification)

  const [verifying, setVerifying] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [resending, setResending] = useState(false)

  useEffect(() => {
    // Supabase handles email verification via redirect with access_token in URL hash
    // When user clicks the verification link, Supabase redirects here with tokens
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const accessToken = hashParams.get('access_token')
    const type = hashParams.get('type')

    if (accessToken && type === 'signup') {
      handleVerification()
    }

    // Also check for token_hash in search params (newer Supabase flow)
    const tokenHash = searchParams.get('token_hash')
    const paramType = searchParams.get('type')
    if (tokenHash && paramType === 'signup') {
      handleVerificationWithToken(tokenHash, paramType)
    }
  }, [])

  const handleVerification = async () => {
    setVerifying(true)
    try {
      // The session should already be set by Supabase from the URL hash
      const { data: { session } } = await supabase.auth.getSession()

      if (session?.user) {
        // Mark user as verified in our public.users table
        await supabase
          .from('users')
          .update({ is_verified: true })
          .eq('id', session.user.id)

        setSuccess(true)
        setVerifying(false)

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login')
        }, 2000)
      } else {
        setError('Verification session not found. Please try logging in.')
        setVerifying(false)
      }
    } catch (err) {
      setError('Verification failed. Please try again.')
      setVerifying(false)
    }
  }

  const handleVerificationWithToken = async (tokenHash, type) => {
    setVerifying(true)
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: type
      })

      if (verifyError) {
        setError(verifyError.message || 'Verification failed. Token may be invalid or expired.')
        setVerifying(false)
        return
      }

      // Mark verified in public.users
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        await supabase
          .from('users')
          .update({ is_verified: true })
          .eq('id', session.user.id)
      }

      setSuccess(true)
      setVerifying(false)

      setTimeout(() => {
        navigate('/login')
      }, 2000)
    } catch (err) {
      setError('Verification failed. Please try again.')
      setVerifying(false)
    }
  }

  const handleResend = async () => {
    if (!email) {
      setError('Email address not found. Please try registering again.')
      return
    }

    setResending(true)
    setError('')

    try {
      await resendVerification(email)
      setResending(false)
      alert('Verification email sent! Check your inbox.')
    } catch (err) {
      setError('Failed to resend email. Please try again.')
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0e17] text-white">
      <Header />
      <div className="flex items-center justify-center px-4 py-12">
        <div className="card max-w-md w-full text-center">
          {verifying ? (
            <>
              <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold mb-2">Verifying Email...</h2>
              <p className="text-gray-400">Please wait while we verify your email address</p>
            </>
          ) : success ? (
            <>
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-2">Email Verified!</h2>
              <p className="text-gray-400 mb-4">
                Your email has been successfully verified. Your 14-day free trial is now active!
              </p>
              <p className="text-gray-500 text-sm">
                Redirecting to login...
              </p>
            </>
          ) : error ? (
            <>
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-2 text-red-400">Verification Failed</h2>
              <p className="text-gray-400 mb-6">{error}</p>
              {email && (
                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                >
                  {resending ? 'Sending...' : 'Resend Verification Email'}
                </button>
              )}
              <div>
                <Link to="/plans" className="text-cyan-400 hover:text-cyan-300 transition">
                  Register again
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-2">Check Your Email</h2>
              <p className="text-gray-400 mb-2">
                We've sent a verification link to{' '}
                {email && <strong className="text-white">{email}</strong>}
              </p>
              <p className="text-gray-500 text-sm mb-6">
                Click the link in the email to verify your account and activate your <strong className="text-cyan-400">14-day free trial</strong>. The link will expire in 24 hours.
              </p>
              {email && (
                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="text-cyan-400 hover:text-cyan-300 transition text-sm disabled:opacity-50"
                >
                  {resending ? 'Sending...' : 'Resend verification email'}
                </button>
              )}
              <div className="mt-6">
                <Link to="/login" className="text-gray-400 hover:text-white transition text-sm">
                  Return to login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  )
}
